import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { BlockThemeConfig, DChartInterface, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import didCacheUpdate from 'webapp/modifiers/did-cache-update';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import LoaderSvgComponent from 'webapp/components/loader-svg';

import type { CustomColorMap } from 'webapp/models-custom/block-setup';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import DChart from 'webapp/d-chart/d-chart';

export interface Args {
	visualization: VisualizationLineChart; // should work with others too
	annotationContext: DChartInterface.AnnotationContext;
	canAnnotate: boolean;
	themeData?: BlockThemeConfig.BlockParsed;
	height?: number;
	width?: number | '100%';
	isSyncing?: boolean;
}

export default abstract class CommonSimpleLineChartComponent<T extends Args> extends Component<T> {
	@service('navigation') declare navigationService: Service.Navigation;

	node: HTMLElement | null = null;
	dChart: DChart | null = null;
	fontSize = 10;

	hideEmptyChart = true;

	heightToWidthRatio = 0.45;

	abstract get themeData(): BlockThemeConfig.BlockParsed;

	get isChartHidden() {
		return this.hideEmptyChart && this.args.visualization.isEmpty;
	}

	get customColorClassMap(): CustomColorMap {
		return {};
	}

	@action
	resizeChart(): void {
		if (!isAlive(this)) {
			return;
		}
		const size = this.getChartSize();
		this.dChart?.setSize(size.width, size.height, this.fontSize);
	}

	@action
	onThemeChange(): void {
		this.dChart?.updateTheme(this.themeData);
	}

	@action
	onCustomColorChange(): void {
		this.dChart?.updateCustomColors(this.customColorClassMap);
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;

		if (!this.isChartHidden) {
			this.createDChart();
		}

		node.addEventListener('resize', this.resizeChart);
	}

	@action
	teardownView(): void {
		this.destroyChart();
		this.node?.removeEventListener('resize', this.resizeChart);
	}

	private createDChart() {
		const node = this.node;

		if (!node) {
			return;
		}

		const { width, height } = this.getChartSize();

		const opts = {
			node,
			visualization: this.args.visualization,
			maxWidth: width,
			maxHeight: height,
			sizeModifier: this.fontSize,
			themeData: this.themeData,
			canAnnotate: this.args.canAnnotate,
			isAnimated: !this.navigationService.isLoadedFromSnapshooter,
			annotationContext: this.args.annotationContext,
			customColorClassMap: this.customColorClassMap,
		};

		this.dChart = new DChart(opts);
	}

	@action
	onDataChange(isInit: boolean): void {
		if (isInit) {
			this.updateDataCache();
		} else {
			// delay it for the case if the parent does updateDataCache on visualization
			next(() => {
				this.updateDataCache();
				if (this.isChartHidden) {
					this.destroyChart();
				} else {
					if (this.dChart) {
						this.dChart.updateData();
					} else {
						this.createDChart();
					}
				}
			});
		}
	}

	private destroyChart(): void {
		if (this.dChart) {
			this.dChart.teardown();
		}
	}

	protected updateDataCache() {
		this.args.visualization.checkToUpdateDataCache();
	}

	// ------------- Sizing helpers ------------

	private getChartSize(): { width: number; height: number } {
		// in case height or width isn't explicitly given
		const heightToWidthRatio = this.heightToWidthRatio,
			node = this.node;

		let height = 0,
			width = 0;

		const isFullWidth = this.args.width === '100%';
		if (node) {
			const nodeWidth = Math.floor(node.offsetWidth);

			const maxWidthNode =
				node.closest('.bc__top-numbers--single') || // single number
				node.closest('.bc__number'); // multinumber
			// forecasts listing small chart is given 100% width so we dont need to list it here

			const maxWidth = (maxWidthNode as HTMLElement)?.offsetWidth || Infinity;

			if (!this.args.height && !this.args.width) {
				// take whole height, and adjust width accordingly
				height = node.offsetHeight;

				const isSingleNumberWithChart = !!node.closest('.bc__top-numbers--single');
				if (isSingleNumberWithChart) {
					// case of small chart under *single* number (which has no height, bcos its centered vertically)
					// we use the maxHeight set on top-numbers::prepareNumberGrid bcos the css of 25% height does not work
					const innerNode = node.closest<HTMLElement>('.bc_top-numbers-inner')!;
					if (innerNode) {
						height = 0.25 * parseInt(innerNode.style.maxHeight) || 0;
					}
				}
				width = Math.min(Math.round(height / heightToWidthRatio), maxWidth);
			} else if (this.args.height) {
				height = this.args.height;
				width = isFullWidth
					? nodeWidth
					: Math.min(Math.round(height / heightToWidthRatio), maxWidth);
			} else if (this.args.width) {
				width = isFullWidth ? nodeWidth : Math.min(this.args.width, maxWidth);
				height = width * heightToWidthRatio;
			}
		}

		return {
			width,
			height,
		};
	}
	<template>
		{{#if @isSyncing}}
			<LoaderSvgComponent />
		{{/if}}
		<div
			class="bc__chart visualization--4 bgTheme-{{this.themeData.bgTheme}}
				colorTheme-{{this.themeData.themeId}}"
			{{didCacheUpdate this.onDataChange @visualization.block.sample.sampledata}}
			{{didInsert this.setupView}}
			{{didUpdate this.onThemeChange this.themeData}}
			{{didUpdate this.onCustomColorChange this.customColorClassMap}}
			{{willDestroy this.teardownView}}
			{{onWindowEvents this.resizeChart "resize" 200}}
		>
		</div>
	</template>
}
